// ============================================================================
// StatStrip / EquationStrip — the two strip patterns (R15, R16, §14)
// ----------------------------------------------------------------------------
// The owner's handoff replaces loose KPI tiles with one joined card:
//
//   StatStrip      In Process | Birds on Farm | Processed 2026 | Total Lbs
//   EquationStrip  On Hand  +  Ordered  −  Need  =  Remainder
//
// Both are ONE card (R08: radius 18, --shadow-card) whose tiles are divided by
// a --hairline rule (R02). A hairline inside a card is structure; it is not the
// side rail R09 removed, and the guard allows 1px neutral edges for this.
//
// A tile shows a label, a big tabular number with its unit as small muted text
// INSIDE the value, and at most one sub-line (R11 — label and number are
// enough; a sub-line is only for data the reader acts on).
//
// Tones are the soft tints of R03, never a solid status fill. One tile per page
// may be the dark `attention` tile (R04).
// ============================================================================
import type {CSSProperties, ReactNode} from 'react';
import React from 'react';

/** Soft tints a tile may take (R03), plus the one dark tile a page may have (R04). */
export type StripTone = 'plain' | 'ok' | 'warn' | 'danger' | 'info' | 'data' | 'attention';

const TONES: Record<StripTone, {background: string; color?: string; label?: string}> = {
  plain: {background: 'transparent'},
  ok: {background: 'var(--ok-soft)', label: 'var(--ok-ink)'},
  warn: {background: 'var(--warn-soft)', label: 'var(--warn-ink)'},
  danger: {background: 'var(--danger-soft)', label: 'var(--danger-ink)'},
  info: {background: 'var(--info-soft)', label: 'var(--info-ink)'},
  data: {background: 'var(--data-soft)', label: 'var(--warn-ink)'},
  attention: {
    background: 'linear-gradient(135deg, var(--attention-from), var(--attention-to))',
    color: '#fff',
    label: 'rgba(255,255,255,.82)',
  },
};

export type StatTile = {
  /** Eyebrow label, written in sentence case; rendered uppercase. */
  label: string;
  /** The number itself. A falsy value renders the muted em dash (R14). */
  value: ReactNode;
  /** Small muted text inside the value ("batches", "lb") — never a separate label (R28). */
  unit?: string;
  /** At most one, and only when it is data the reader acts on (R11). */
  sub?: ReactNode;
  tone?: StripTone;
  /** Test hook, spread onto the tile. */
  attrs?: Record<string, string>;
};

const cardStyle: CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 18,
  boxShadow: 'var(--shadow-card)',
  overflow: 'hidden',
};

const labelStyle: CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '.06em',
  textTransform: 'uppercase',
  color: 'var(--label)',
};

const valueStyle: CSSProperties = {
  fontSize: 30,
  fontWeight: 800,
  letterSpacing: '-.02em',
  marginTop: 6,
  fontVariantNumeric: 'tabular-nums',
};

const unitStyle: CSSProperties = {
  fontSize: 15,
  fontWeight: 600,
  color: 'var(--text-2)',
  letterSpacing: 0,
};

/** An empty value is a muted em dash, never a blank (R14). */
function orDash(value: ReactNode): ReactNode {
  const empty = value === null || value === undefined || value === '';
  return empty ? <span style={{color: 'var(--label)'}}>—</span> : value;
}

export function StatStrip({
  tiles,
  rootAttrs,
}: {
  tiles: readonly StatTile[];
  rootAttrs?: Record<string, string>;
}): React.ReactElement | null {
  if (!tiles.length) return null;
  return (
    <div
      data-stat-strip={String(tiles.length)}
      {...rootAttrs}
      style={{...cardStyle, display: 'grid', gridTemplateColumns: `repeat(${tiles.length}, 1fr)`}}
    >
      {tiles.map((tile, i) => {
        const tone = TONES[tile.tone ?? 'plain'];
        return (
          <div
            key={tile.label}
            data-stat-tile={tile.label}
            {...tile.attrs}
            style={{
              padding: '18px 22px',
              // The divider between tiles: structure inside one card (R02).
              borderLeft: i === 0 ? undefined : '1px solid var(--hairline)',
              background: tone.background,
              color: tone.color,
            }}
          >
            <div style={{...labelStyle, color: tone.label ?? labelStyle.color}}>{tile.label}</div>
            <div style={valueStyle}>
              {orDash(tile.value)}
              {tile.unit ? (
                <span style={{...unitStyle, color: tone.color ?? unitStyle.color}}> {tile.unit}</span>
              ) : null}
            </div>
            {tile.sub ? (
              <div
                style={{
                  fontSize: 13,
                  color: tone.color ?? 'var(--text-2)',
                  marginTop: 4,
                  opacity: tone.color ? 0.86 : 1,
                }}
              >
                {tile.sub}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export type EquationTerm = StatTile & {
  /** The operator printed BEFORE this term. The first term has none. */
  operator?: '+' | '−' | '=';
};

/**
 * `A + B − C = D` in one card. Operators sit in their own narrow cells in
 * --operator, large and light, so they read as arithmetic rather than as text.
 */
export function EquationStrip({
  terms,
  rootAttrs,
}: {
  terms: readonly EquationTerm[];
  rootAttrs?: Record<string, string>;
}): React.ReactElement | null {
  if (!terms.length) return null;
  const columns = terms.map((t, i) => (i === 0 || !t.operator ? '1fr' : '40px 1fr')).join(' ');
  return (
    <div
      data-equation-strip={String(terms.length)}
      {...rootAttrs}
      style={{...cardStyle, display: 'grid', gridTemplateColumns: columns, alignItems: 'stretch'}}
    >
      {terms.map((term, i) => {
        const tone = TONES[term.tone ?? 'plain'];
        return (
          <React.Fragment key={term.label}>
            {i > 0 && term.operator ? (
              <div
                aria-hidden="true"
                data-equation-operator={term.operator}
                style={{
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 26,
                  fontWeight: 300,
                  color: 'var(--operator)',
                }}
              >
                {term.operator}
              </div>
            ) : null}
            <div
              data-equation-term={term.label}
              {...term.attrs}
              style={{padding: '18px 22px', background: tone.background, color: tone.color}}
            >
              <div
                style={{...labelStyle, fontSize: 11.5, letterSpacing: '.08em', color: tone.label ?? labelStyle.color}}
              >
                {term.label}
              </div>
              <div style={{...valueStyle, letterSpacing: '-.03em'}}>
                {orDash(term.value)}
                {term.unit ? (
                  <span style={{...unitStyle, fontSize: 14, color: tone.color ?? 'var(--label)'}}> {term.unit}</span>
                ) : null}
              </div>
              {term.sub ? (
                <div style={{fontSize: 13, color: tone.color ?? 'var(--text-2)', marginTop: 4}}>{term.sub}</div>
              ) : null}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}
